import { Truck } from 'lucide-react';
import styles from './ShippingInfo.module.css';
import type { ShippingInfoProps } from './ShippingInfo.types';

/**
 * Calculates total estimated delivery range from turnaround and shipping day ranges.
 * Each input is a string like "3-5" or a single number like "3".
 */
function calculateDeliveryRange(turnaroundDays: string, shippingDays: string): string {
  const parseDayRange = (range: string): { min: number; max: number } => {
    const parts = range.split('-').map(Number);
    const first = parts[0] ?? NaN;
    const second = parts[1] ?? NaN;
    if (parts.length === 2 && !Number.isNaN(first) && !Number.isNaN(second)) {
      return { min: first, max: second };
    }
    const single = Number(range);
    if (!Number.isNaN(single)) {
      return { min: single, max: single };
    }
    return { min: 0, max: 0 };
  };

  const turnaround = parseDayRange(turnaroundDays);
  const shipping = parseDayRange(shippingDays);

  const minDays = turnaround.min + shipping.min;
  const maxDays = turnaround.max + shipping.max;

  if (minDays === maxDays) {
    return `${minDays}`;
  }
  return `${minDays}-${maxDays}`;
}

export function ShippingInfo({
  turnaroundDays,
  shippingDays,
  variant = 'inline',
}: ShippingInfoProps) {
  const deliveryRange = calculateDeliveryRange(turnaroundDays, shippingDays);

  return (
    <p className={`${styles.shippingInfo} ${styles[variant]}`} aria-label="Shipping estimate">
      <Truck size={14} className={styles.icon} aria-hidden="true" />
      <span>
        {variant === 'banner'
          ? `Estimated delivery: ${deliveryRange} business days`
          : `Ships in ${deliveryRange} business days`}
      </span>
    </p>
  );
}
